import Accordion from '@/common/components/Accordion'
import Checkbox from '@/common/components/Checkbox'
import Select from '@/common/components/Select'
import { useProductCategories } from '@/common/hooks/queries/useProductCategories'
import useDebounce from '@/common/hooks/useDebounce'
import { Mapper } from '@/common/services/mapper'
import { ProductCategoryData } from '@/common/types/productCategory.type'
import { ContractProductCategoryItem } from '@/features/publicCompanyLeadsFunnel/components/ContractProductCategoryItem'
import { CreateContractProductCategoryModal } from '@/features/publicCompanyLeadsFunnel/components/CreateContractProductCategoryModal'
import { useUpdatePublicCompanyLeadContractProductRequireProductCreditQuota } from '@/features/publicCompanyLeadsFunnel/hooks/useUpdatePublicCompanyLeadContractProductRequireProductCreditQuota'
import { useIsFetching, useIsMutating, useQueryClient } from '@tanstack/react-query'
import { InfoIcon } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'
import { PublicCompanyLeadData } from '../types/publicCompanyLead'
import SelectController from '@/common/components/SelectController'
import { useFormContext } from 'react-hook-form'
import { PublicCompanyLeadsFunnelUpdateContractValidationSchema } from '../schemas/update.schema'
import { getPriceRuleOptions } from '../constants/priceRuleOptions.constant'

interface ContractProductCategoriesProps {
  lead?: PublicCompanyLeadData
  submit: () => void
  disabled?: boolean
}

export const ContractProductCategories = ({ lead, submit, disabled }: ContractProductCategoriesProps) => {
  const { publicCompanyLeadId } = useParams()

  const { control, formState: { errors } } = useFormContext<PublicCompanyLeadsFunnelUpdateContractValidationSchema>();

  const priceRuleOptions = useMemo(() => getPriceRuleOptions({ isPrivateFuel: true }), [])

  const queryClient = useQueryClient()

  const [productCategory, setProductCategory] = useState<ProductCategoryData>()

  const [searchProductCategories, setSearchProductCategories] = useDebounce('')

  const {
    mutate: updateContractProductRequireProductCreditQuota,
    isPending: updateContractProductRequireProductCreditQuotaIsPending,
  } = useUpdatePublicCompanyLeadContractProductRequireProductCreditQuota({
    publicCompanyLeadId: publicCompanyLeadId,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })
    },
  })

  const publicCompanyIsFetching = useIsFetching({ queryKey: ['publicCompanyLead', publicCompanyLeadId] })

  const deletePublicCompanyLeadProductCategoryIsPending = useIsMutating({
    mutationKey: ['deletePublicCompanyLeadProductCategory'],
  })

  const deletePublicCompanyLeadProductCategoryProductIsPending = useIsMutating({
    mutationKey: ['deletePublicCompanyLeadProductCategoryProduct'],
  })

  const { data: productCategoryOptions } = useProductCategories({
    filters: { search: searchProductCategories },
    mapper: Mapper.mapToOptionsWithItem<ProductCategoryData>({ labelFieldName: 'display_name', valueFieldName: 'id' }),
  })

  return (
    <Accordion
      name='contract_product_categories'
      className="py-1"
      contentClassName="overflow-visible"
      head={<p>Detalhamento do Valor global e Regras de preços</p>}
      body={
        <div className="flex flex-col gap-4 overflow-visible">
          <Checkbox
            label="Informar valores de cota"
            name="require_product_credit_quota"
            checked={!!lead?.contract?.require_product_credit_quota}
            disabled={disabled || updateContractProductRequireProductCreditQuotaIsPending}
            onChange={(event) => {
              updateContractProductRequireProductCreditQuota(event.currentTarget.checked)
            }}
          />

          <Select
            name='search_contract_product_categories'
            placeholder="Pesquisar Serviços"
            value={null}
            onInputChange={(value) => setSearchProductCategories(value)}
            onChange={(newValue) => {
              setProductCategory((newValue as { item: ProductCategoryData }).item)
            }}
            options={
              productCategoryOptions?.filter(
                (option) =>
                  !lead?.contract?.contract_product_categories?.some(
                    (contractProductCategory) => contractProductCategory?.product_category?.id === option.value,
                  ),
              ) || []
            }
            isDisabled={
              !!publicCompanyIsFetching ||
              !!deletePublicCompanyLeadProductCategoryIsPending ||
              !!deletePublicCompanyLeadProductCategoryProductIsPending ||
              disabled
            }
            isLoading={!!publicCompanyIsFetching}
          />

          <div className="flex items-center justify-between">
            <div className="flex gap-2 items-center">
              <InfoIcon strokeWidth={1} color="var(--warning)" />

              <div>
                <p className="text-[10px] font-bold leading-3">
                  Ao selecionar um Serviço e/ou Produto, todos os não selecionados serão bloqueados para consumo
                </p>
                <p className="text-[10px] font-light italic leading-3">
                  Certifique-se de adicionar as restrições para todos os serviços e produtos que desejar consumir.
                </p>
              </div>
            </div>
          </div>

          {lead?.contract?.contract_product_categories?.map((contractProductCategory, index) => (
            <ContractProductCategoryItem
              editable={
                !(
                  !!publicCompanyIsFetching ||
                  !!deletePublicCompanyLeadProductCategoryIsPending ||
                  !!deletePublicCompanyLeadProductCategoryProductIsPending ||
                  disabled
                )
              }
              key={contractProductCategory.product_category.id}
              lead={lead}
              productCategoryIndex={index}
              contractProductCategory={contractProductCategory}
            />
          ))}

          <CreateContractProductCategoryModal
            lead={lead}
            productCategory={productCategory}
            setProductCategory={setProductCategory}
          />

          <SelectController
            control={control}
            name="contract.default_fuel_price_rule"
            label="Regra de Preço (Padrão)"
            defaultValue={priceRuleOptions.find((option) => option.value === lead?.contract?.default_fuel_price_rule)}
            options={priceRuleOptions || []}
            triggerOnChange={submit}
            isDisabled={disabled}
            errorMessage={errors?.contract?.default_fuel_price_rule?.message}
          />
        </div>
      }
    />
  )
}
